import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { axiosClient } from '@/services/BaseApi.service';

import type {
  HostIsolationAssetOption,
  HostIsolationAssetSearchParams
} from './HostIsolationExclusions.types';

const HOST_ASSET_SEARCH_LIMIT = 50;

const hostIsolationAssetQueryKeys = {
  search: ({ organizationLabel, term }: HostIsolationAssetSearchParams) =>
    ['host-isolation-assets', organizationLabel, term] as const
};

const fetchHostIsolationAssets = async (
  organizationLabel: string,
  term: string,
  skip: number,
  signal: AbortSignal
) =>
  (
    await axiosClient.get<HostIsolationAssetOption[]>(
      `/organizations/${organizationLabel}/assets`,
      {
        params: {
          asset_name: term,
          limit: HOST_ASSET_SEARCH_LIMIT,
          skip
        },
        signal
      }
    )
  ).data;

export const useDebouncedHostAssetSearchTerm = (value: string) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedValue(value.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [value]);

  return debouncedValue;
};

export const useHostIsolationAssetSearch = ({
  enabled,
  organizationLabel,
  term
}: HostIsolationAssetSearchParams & { enabled: boolean }) =>
  useInfiniteQuery({
    queryKey: hostIsolationAssetQueryKeys.search({ organizationLabel, term }),
    queryFn: ({ pageParam, signal }) =>
      fetchHostIsolationAssets(organizationLabel, term, pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) =>
      lastPage.length === HOST_ASSET_SEARCH_LIMIT
        ? lastPageParam + lastPage.length
        : undefined,
    enabled: enabled && Boolean(term),
    staleTime: 60 * 1000
  });

export { HOST_ASSET_SEARCH_LIMIT };
